import * as Popover from '@radix-ui/react-popover';
import { Check, ChevronDown, Loader2 } from 'lucide-react';
import { cn, getInitials } from '@/lib/utils';
import { useAsignables, useEtiquetas } from '@/hooks/useTareas';
import type { UsuarioAsignable } from '@/types/tareas';
import { EtiquetaChip } from './TareaPiezas';

const campo = 'w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 bg-white';

function nombreCompleto(u: Pick<UsuarioAsignable, 'nombre' | 'apellidoPaterno'>) {
  return [u.nombre, u.apellidoPaterno].filter(Boolean).join(' ');
}

/* ── Asignados ───────────────────────────────────────────────────────────── */

export function AsignadosPicker({
  value, onChange, disabled, open, onOpenChange,
}: { value: string[]; onChange: (ids: string[]) => void; disabled?: boolean; open?: boolean; onOpenChange?: (open: boolean) => void }) {
  const { data: usuarios = [], isLoading } = useAsignables();
  const seleccionados = usuarios.filter(u => value.includes(u.id));

  const alternar = (id: string) =>
    onChange(value.includes(id) ? value.filter(v => v !== id) : [...value, id]);

  return (
    <Popover.Root open={open} onOpenChange={onOpenChange}>
      <Popover.Trigger asChild disabled={disabled}>
        <button type="button" className={cn(campo, 'flex items-center justify-between gap-2 text-left disabled:opacity-60')}>
          <span className="truncate text-sm">
            {seleccionados.length ? seleccionados.map(nombreCompleto).join(', ') : <span className="text-gray-400">Sin asignar</span>}
          </span>
          <ChevronDown size={14} className="text-gray-400 flex-shrink-0" />
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content align="start" sideOffset={4} className="z-[70] w-72 max-h-72 overflow-y-auto rounded-xl border border-gray-200 bg-white shadow-lg p-1">
          {isLoading && <div className="p-3 text-sm text-gray-400 flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Cargando…</div>}
          {usuarios.map(u => {
            const activo = value.includes(u.id);
            return (
              <button
                type="button"
                key={u.id}
                onClick={() => alternar(u.id)}
                className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-gray-50 text-left"
              >
                <span className="w-7 h-7 rounded-full bg-primary-100 text-primary-700 text-[11px] font-semibold flex items-center justify-center overflow-hidden flex-shrink-0">
                  {u.fotoUrl ? <img src={u.fotoUrl} alt="" className="w-full h-full object-cover" /> : getInitials(nombreCompleto(u))}
                </span>
                <span className="flex-1 text-sm text-text-primary truncate">{nombreCompleto(u)}</span>
                {activo && <Check size={14} className="text-primary-600" />}
              </button>
            );
          })}
          {!isLoading && !usuarios.length && <p className="p-3 text-sm text-gray-400">No hay usuarios disponibles.</p>}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

/* ── Etiquetas ───────────────────────────────────────────────────────────── */

export function EtiquetasPicker({
  value, onChange, disabled, open, onOpenChange,
}: { value: string[]; onChange: (ids: string[]) => void; disabled?: boolean; open?: boolean; onOpenChange?: (open: boolean) => void }) {
  const { data: etiquetas = [], isLoading } = useEtiquetas();
  const seleccionadas = etiquetas.filter(e => value.includes(e.id));

  const alternar = (id: string) =>
    onChange(value.includes(id) ? value.filter(v => v !== id) : [...value, id]);

  return (
    <Popover.Root open={open} onOpenChange={onOpenChange}>
      <Popover.Trigger asChild disabled={disabled}>
        <button type="button" className={cn(campo, 'flex items-center justify-between gap-2 text-left disabled:opacity-60 min-h-[2.5rem]')}>
          <span className="flex flex-wrap gap-1">
            {seleccionadas.length
              ? seleccionadas.map(e => <EtiquetaChip key={e.id} etiqueta={e} />)
              : <span className="text-gray-400 text-sm">Sin etiquetas</span>}
          </span>
          <ChevronDown size={14} className="text-gray-400 flex-shrink-0" />
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content align="start" sideOffset={4} className="z-[70] w-64 max-h-64 overflow-y-auto rounded-xl border border-gray-200 bg-white shadow-lg p-1">
          {isLoading && <div className="p-3 text-sm text-gray-400 flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Cargando…</div>}
          {etiquetas.map(e => (
            <button
              type="button"
              key={e.id}
              onClick={() => alternar(e.id)}
              className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-gray-50 text-left"
            >
              <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: e.color }} />
              <span className="flex-1 text-sm text-text-primary truncate">{e.nombre}</span>
              {value.includes(e.id) && <Check size={14} className="text-primary-600" />}
            </button>
          ))}
          {!isLoading && !etiquetas.length && (
            <p className="p-3 text-sm text-gray-400">Aún no hay etiquetas. Un administrador puede crearlas en Configuración → Tareas.</p>
          )}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

